// Speech Recognition Service for Voice Input
class SpeechRecognitionService {
  private recognition: any = null;
  private isListening = false;
  private isBrowserSupported = false;

  constructor() {
    // Check browser support
    const SpeechRecognition =
      (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;

    if (SpeechRecognition) {
      this.isBrowserSupported = true;
      this.recognition = new SpeechRecognition();
      this.setupRecognition();
    }
  }

  private setupRecognition(): void {
    if (!this.recognition) return;

    this.recognition.continuous = false;
    this.recognition.interimResults = true;
    this.recognition.lang = 'en-US';
    this.recognition.maxAlternatives = 1;
  }

  isSupported(): boolean {
    return this.isBrowserSupported;
  }

  getIsListening(): boolean {
    return this.isListening;
  }

  startListening(
    onResult: (transcript: string, isFinal: boolean) => void,
    onError?: (error: string) => void,
    onStart?: () => void,
    onEnd?: () => void
  ): void {
    if (!this.recognition) return;

    // If already listening, stop first
    if (this.isListening) {
      this.stopListening();
    }

    this.recognition.onstart = () => {
      this.isListening = true;
      if (onStart) onStart();
    };

    this.recognition.onresult = (event: any) => {
      const last = event.results.length - 1;
      const result = event.results[last];
      const transcript = result[0].transcript;
      const isFinal = result.isFinal;

      onResult(transcript, isFinal);
    };

    this.recognition.onerror = (event: any) => {
      console.error('Speech recognition error:', event.error);
      this.isListening = false;
      const errorMessage = this.getErrorMessage(event.error);
      if (onError) onError(errorMessage);
    };

    this.recognition.onend = () => {
      this.isListening = false;
      if (onEnd) onEnd();
    };

    try {
      this.recognition.start();
    } catch (error: any) {
      console.error('Failed to start recognition:', error);
      this.isListening = false;

      // If recognition is already started, reset it
      if (error.message && error.message.includes('already started')) {
        this.stopListening();
        // Try again after a brief delay
        setTimeout(() => {
          try {
            this.recognition.start();
          } catch (retryError) {
            if (onError) onError('Failed to start voice recognition');
          }
        }, 100);
      } else {
        if (onError) onError('Failed to start voice recognition');
      }
    }
  }

  stopListening(): void {
    if (this.recognition && this.isListening) {
      this.recognition.stop();
      this.isListening = false;
    }
  }

  private getErrorMessage(error: string): string {
    switch (error) {
      case 'no-speech':
        return 'No speech detected. Please try again.';
      case 'audio-capture':
        return 'Microphone not found or not accessible.';
      case 'not-allowed':
        return 'Microphone permission denied. Please enable microphone access.';
      case 'network':
        return 'Network error occurred during speech recognition.';
      case 'aborted':
        return 'Speech recognition was aborted.';
      default:
        return 'Speech recognition error occurred.';
    }
  }

  abort(): void {
    if (this.recognition && this.isListening) {
      this.recognition.abort();
      this.isListening = false;
    }
  }
}

export const speechRecognitionService = new SpeechRecognitionService();
